import React, { useState } from 'react'
import { Mail, Send, Check, AlertTriangle, Loader2, MessageSquare } from 'lucide-react'
import { contactAPI } from '../../services/api'
import { OWNER_EMAIL } from '../../components/Footer'
import { PageHero } from './_shared'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState('idle')  // idle | sending | sent | error
  const [error, setError] = useState(null)

  const valid = form.name.trim() && EMAIL_RE.test(form.email.trim()) && form.message.trim().length >= 5
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    if (!valid || status === 'sending') return
    setStatus('sending'); setError(null)
    try {
      const { data } = await contactAPI.send(form)
      if (data?.delivered) {
        setStatus('sent')
        setForm({ name: '', email: '', subject: '', message: '' })
      } else {
        setStatus('error')
        setError(data?.message || 'Message could not be sent.')
      }
    } catch (err) {
      setStatus('error')
      setError(err.response?.data?.message || 'Message could not be sent right now.')
    }
  }

  return (
    <>
      <PageHero
        kicker="Contact"
        title="Questions, feedback, or just hello."
        lede="Reach out about the product, a bug, or an idea. Messages come straight to the inbox — no ticket queue, no bots."
      />

      <section className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left — direct channels */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card">
              <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-3">
                <Mail className="w-5 h-5 text-accent-600" />
              </div>
              <h3 className="font-semibold text-ink-900">Email directly</h3>
              <p className="mt-1.5 text-sm text-ink-500 leading-6">
                Prefer your own mail client? Write any time and you’ll get a personal reply.
              </p>
              <a href={`mailto:${OWNER_EMAIL}`} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-accent-700 hover:text-accent-800">
                <Mail className="w-4 h-4" /> {OWNER_EMAIL}
              </a>
            </div>
            <div className="card">
              <div className="w-10 h-10 rounded-xl bg-accent-50 flex items-center justify-center mb-3">
                <MessageSquare className="w-5 h-5 text-accent-600" />
              </div>
              <h3 className="font-semibold text-ink-900">What to expect</h3>
              <p className="mt-1.5 text-sm text-ink-500 leading-6">
                serenDPT is a personal project, so replies are personal too — thoughtful, but
                not instant. Bug reports with steps are especially welcome.
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="card space-y-4">
              {status === 'sent' && (
                <div className="flex items-start gap-2.5 rounded-xl border border-gain-100 bg-gain-50 px-4 py-3 text-sm text-gain-700">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Thanks — your message has been sent. You’ll hear back at the email you provided.</span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {error}{' '}
                    Please email us directly at{' '}
                    <a href={`mailto:${OWNER_EMAIL}`} className="font-semibold underline">{OWNER_EMAIL}</a>.
                  </span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Name</label>
                  <input className="input" value={form.name} onChange={set('name')} placeholder="Your name" maxLength={120} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Email</label>
                  <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" maxLength={160} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Subject <span className="text-ink-400 font-normal">(optional)</span></label>
                <input className="input" value={form.subject} onChange={set('subject')} placeholder="What’s this about?" maxLength={160} />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Message</label>
                <textarea className="input min-h-[140px] resize-y" value={form.message} onChange={set('message')} placeholder="Tell us what’s on your mind…" maxLength={5000} required />
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <span className="text-xs text-ink-400">{form.message.length}/5000</span>
                <button type="submit" disabled={!valid || status === 'sending'} className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {status === 'sending'
                    ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                    : (<><Send className="w-4 h-4" /> Send message</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  )
}
